import { Formik } from 'formik';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { Team } from 'modules/teams/domain/interfaces/team';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import {
  TeamSelectComponent,
  teamToTeamOption,
} from 'modules/teams/ui/components/add-team-application-form/add-team-application-form';
import { LoadingMessage } from 'modules/teams/ui/hooks/use-modal-add-player/styles';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { CreatableSelectOption } from 'ud-form/components/select/component';
import Modal from 'ud-ui/components/modal';
import UDButton from 'ud-ui/components/button';
import { Player } from 'modules/players/domain/interfaces/player';
import { addTournamentUserToTeam } from 'modules/players/store/actions';

type FormValues = {
  team?: CreatableSelectOption<any, Team>;
};

function useModalAddToTeam() {
  const [isOpen, setIsOpen] = useState(false);
  const { info } = useOrganizer();
  const dispatch = useDispatch<AppDispatch>();

  const [player, setPlayer] = useState<Player>();

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleLoadOptions = useCallback(
    async (inputValue: string) => {
      if (!info?.id) return [];
      const teams = await teamsRepository.getTournamentTeams({
        tournamentId: info.id,
        limit: 10,
        offset: 0,
        sportType: info?.sportType ?? SportType.football,
        namePart: inputValue,
      });

      return teams.map(teamToTeamOption);
    },
    [info],
  );

  const handleClickTeam = useCallback(
    (teamId: number) => {
      if (!player?.id) {
        toast.error('Не удалось определить игрока, перезагрузите страницу и попробуйте снова');
        return;
      }

      if (player.positionsInTeams.some((position) => position?.team?.id === teamId)) {
        toast.warn('Игрок уже состоит в команде');
        return;
      }

      dispatch(addTournamentUserToTeam({ teamId: teamId, userId: player.id }))
        .unwrap()
        .then(() => {
          close();
          toast.success('Игрок добавлен в команду');
        })
        .catch((error) => {
          console.error(error);
          if (error === 'User is already in this team') {
            toast.warn('Игрок уже состоит в команде');
          } else {
            toast.error('Ошибка при добавлении игрока в команду, попробуйте ещё раз или обратитесь в поддержку');
          }
        });
    },
    [close, dispatch, player],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      const { team } = values;
      if (!team?.data?.id) {
        return;
      }

      handleClickTeam(team.data.id);
    },
    [handleClickTeam],
  );

  const Component = useCallback(() => {
    return (
      <Formik<FormValues> initialValues={{ team: undefined }} onSubmit={handleSubmit}>
        {(formProps) => {
          return (
            <Modal show={isOpen} onClose={close}>
              <form onSubmit={formProps.handleSubmit}>
                <Modal.Header title="Добавление в команду" />

                <Modal.Body className={'h-[180px] min-w-80'}>
                  <AsyncSelect
                    key="add-new-team"
                    className="w-full"
                    name="team"
                    placeholder="Введите название"
                    loadingMessage={LoadingMessage}
                    components={TeamSelectComponent}
                    cacheOptions
                    loadOptions={handleLoadOptions}
                    value={formProps.values.team}
                    onChange={(option) => formProps.setFieldValue('team', option)}
                    onBlur={() => formProps.setFieldTouched('team', true)}
                  />
                </Modal.Body>
                <Modal.Footer>
                  <UDButton
                    type="submit"
                    variant="primary"
                    loading={formProps.isSubmitting}
                    stretch
                    disabled={!formProps.values.team || !formProps.dirty}
                  >
                    Добавить
                  </UDButton>
                </Modal.Footer>
              </form>
            </Modal>
          );
        }}
      </Formik>
    );
  }, [close, handleLoadOptions, handleSubmit, isOpen]);

  const openModal = useCallback(
    (player: Player) => {
      setPlayer(player);

      open();
    },
    [open],
  );

  return { open: openModal, close, Modal: Component };
}

export default useModalAddToTeam;
